import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { INVESTOR_BACKUP_PROPERTY } from '@dealstack/frontend/investor/pages/Investment/common/useBackupController';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';
import { getMainAddress } from '@dealstack/frontend/shared/address.ts';
import { useParams } from 'react-router-dom';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { updateInvestorVehicleData } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import {
	useTranslationCommonForm,
	useTranslationInvestor,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { isTinInUseError } from '@dealstack/frontend/investor/pages/Investment/common/taxErrors.utils';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { isSameStringKeyInsensitive } from '@dealstack/shared/lib/format/string';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useCallback, useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import type { PIVInfoForm } from './useJoinAndAllocationPIVInformationController.types';
import { SIGNATORY_CHOICE } from './useJoinAndAllocationPIVInformationController.types';
import {
	buildControllingPartyEntrySchema,
	fromControllingPartyResList,
	hasDuplicateControllingPartyType,
	inferSignatoryChoice,
	resolveAuthorisedSignatory,
	toControllingPartyDtos,
} from './controllingParties.utils';

export enum JOIN_ALLOCATION_PIV_INFO_FORM {
	IS_PIV = 'isPIV',
	IS_REPRESENTATIVE_SAME_AS_INVESTOR = 'isRepresentativeSameAsInvestor',
	PIV_NAME = 'pivName',
	PIV_REG_NUMBER = 'pivRegNumber',
	PIV_COUNTRY = 'pivCountry',
	PIV_CITY = 'pivCity',
	PIV_POST_CODE = 'pivPostCode',
	PIV_STREET = 'pivStreet',
	PIV_HOUSE_NUMBER = 'pivHouseNumber',
	REPRESENTATIVE_FIRST_NAME = 'representativeFirstName',
	REPRESENTATIVE_LAST_NAME = 'representativeLastName',
	REPRESENTATIVE_EMAIL = 'representativeEmail',
	// Controlling Parties flow (gated behind the "Collect controlling party
	// information" programme setting — MIP-2558). When the setting is OFF these
	// fields stay empty and the legacy representative section above is used.
	IS_CONTROLLING_PARTY_REQUIRED = 'isControllingPartyRequired',
	CONTROLLING_PARTIES = 'controllingParties',
	SIGNATORY_CHOICE = 'signatoryChoice',
	OTHER_SIGNATORY_FIRST_NAME = 'otherSignatoryFirstName',
	OTHER_SIGNATORY_LAST_NAME = 'otherSignatoryLastName',
	OTHER_SIGNATORY_EMAIL = 'otherSignatoryEmail',
	// The already-saved Legal Representative (when it's a distinct person — not
	// the investor and not one of the controlling parties), surfaced as its own
	// pre-selected signatory option.
	SAVED_SIGNATORY = 'savedSignatory',
}

export const useJoinAndAllocationPIVInformationController = () => {
	const tInvestor = useTranslationInvestor();
	const tCommonForm = useTranslationCommonForm();
	const updateInvestorVehicleDataRequest = useWrappedRequest(updateInvestorVehicleData);
	const { kycWorkflowId } = useParams();

	const {
		setIsLoading,
		setIsContinueDisabled,
		getInvestorDataRequest,
		dataBackup,
		updateBackupData,
		removeBackupData,
		kycSetting,
	} = useContext(investmentJoinAndAllocationContext);
	const queryClient = useQueryClient();
	const commonYup = useCommonYupValidation();

	// MIP-2558: when the "Collect controlling party information" programme
	// setting is ON, the legacy "authorised to sign" section is replaced by the
	// Controlling Parties + Authorised Signatory flow. OFF → behaviour unchanged.
	const isControllingPartyRequired = kycSetting?.isControllingPartyInfoRequired ?? false;

	const requiredIfPIV = useCallback(() => {
		return commonYup.string.optional.when([JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV], {
			is: true,
			then: () => commonYup.string.required,
		});
	}, []);

	// Legacy representative fields are only required when the controlling party
	// flow is OFF (otherwise the Authorised Signatory section handles the rep).
	const requiredIfRepresentativeNotSameAsInvestor = useCallback(() => {
		return commonYup.string.optional.when([JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV], {
			is: true,
			then: () =>
				commonYup.string.optional.when(
					[JOIN_ALLOCATION_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED],
					{
						is: false,
						then: () =>
							commonYup.string.optional.when(
								[JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR],
								{
									is: false,
									then: () => commonYup.string.required,
								}
							),
					}
				),
		});
	}, []);

	// "Someone else" signatory fields are required only when investing via a
	// vehicle with the controlling party flow ON and the "someone else" option
	// picked — when investing personally the section is disabled, so its fields
	// must never block Continue (MIP-2667).
	const requiredIfOtherSignatory = useCallback((schema: Yup.StringSchema) => {
		return commonYup.string.optional.when(
			[
				JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV,
				JOIN_ALLOCATION_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED,
				JOIN_ALLOCATION_PIV_INFO_FORM.SIGNATORY_CHOICE,
			],
			{
				is: (isPiv: boolean, isRequired: boolean, choice: string) =>
					isPiv && isRequired && choice === SIGNATORY_CHOICE.OTHER,
				then: () => schema,
			}
		);
	}, []);

	const pivInformationForm = useForm<PIVInfoForm>({
		mode: 'onChange',
		defaultValues: {
			isPIV: false,
			isRepresentativeSameAsInvestor: true,
			isControllingPartyRequired: false,
			controllingParties: [],
			signatoryChoice: SIGNATORY_CHOICE.SELF,
			otherSignatoryFirstName: '',
			otherSignatoryLastName: '',
			otherSignatoryEmail: '',
			savedSignatory: null,
		},
		resolver: yupResolver(
			Yup.object().shape({
				[JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV]: Yup.boolean(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_NAME]: requiredIfPIV(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_REG_NUMBER]: requiredIfPIV(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_COUNTRY]: requiredIfPIV(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_CITY]: requiredIfPIV(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_POST_CODE]: requiredIfPIV(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_STREET]: requiredIfPIV(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_HOUSE_NUMBER]: requiredIfPIV(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME]:
					requiredIfRepresentativeNotSameAsInvestor(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME]:
					requiredIfRepresentativeNotSameAsInvestor(),
				[JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_EMAIL]:
					requiredIfRepresentativeNotSameAsInvestor(),
				// Controlling Parties flow (MIP-2558). Entry rules are shared with the
				// leave flow — see buildControllingPartyEntrySchema. Gated on the
				// programme setting AND on investing via a vehicle: with either off
				// the section never renders (or is disabled), so validating those
				// entries would block Continue on invisible fields (MIP-2667).
				[JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES]: Yup.array().when(
					[
						JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV,
						JOIN_ALLOCATION_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED,
					],
					{
						is: (isPiv: boolean, isRequired: boolean) => isPiv && isRequired,
						then: () =>
							Yup.array().of(
								buildControllingPartyEntrySchema(commonYup, tCommonForm)
							),
					}
				),
				[JOIN_ALLOCATION_PIV_INFO_FORM.SIGNATORY_CHOICE]: commonYup.string.optional.when(
					[
						JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV,
						JOIN_ALLOCATION_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED,
					],
					{
						is: (isPiv: boolean, isRequired: boolean) => isPiv && isRequired,
						then: () => commonYup.string.required,
					}
				),
				[JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME]:
					requiredIfOtherSignatory(commonYup.string.required),
				[JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME]: requiredIfOtherSignatory(
					commonYup.string.required
				),
				[JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL]: requiredIfOtherSignatory(
					commonYup.email.required
				),
			})
		),
	});

	useEffect(() => {
		if (dataBackup.pivInformation) {
			pivInformationForm.reset(dataBackup.pivInformation);
			void pivInformationForm.trigger().then(() => pivInformationForm.clearErrors());
			return;
		}

		const payload = getInvestorDataRequest?.response?.payload;

		if (!payload) {
			return;
		}

		const activePivPerson = payload.pivPerson;
		const pivFormInputFields = activePivPerson || payload.draftPivPerson;
		const isRepresentativeSameAsInvestor = payload.representativePerson?.id
			? isSameStringKeyInsensitive(payload.person?.id ?? '', payload.representativePerson?.id)
			: true;

		const pivMainAddress = getMainAddress(pivFormInputFields?.addresses);

		// Repopulate the controlling parties + authorised signatory from what was
		// previously saved (MIP-2558).
		const controllingPartyEntries = fromControllingPartyResList(
			payload.controllingParties ?? []
		);
		const signatoryChoice = inferSignatoryChoice(
			controllingPartyEntries,
			payload.representativePerson?.id,
			payload.personId
		);
		// A distinct saved Legal Representative becomes its own selected option.
		const savedSignatory =
			signatoryChoice === SIGNATORY_CHOICE.EXISTING_REPRESENTATIVE
				? {
						firstName: payload.representativePerson?.firstName ?? '',
						lastName: payload.representativePerson?.lastName ?? '',
						email: payload.representativePerson?.email ?? '',
					}
				: null;

		pivInformationForm.reset({
			[JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV]: !!activePivPerson,
			[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_NAME]: pivFormInputFields?.displayName,
			[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_REG_NUMBER]:
				pivFormInputFields?.registrationDetails?.corporateRegistrationNumber,
			[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_COUNTRY]: pivMainAddress?.country ?? undefined,
			[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_CITY]: pivMainAddress?.city ?? undefined,
			[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_POST_CODE]: pivMainAddress?.postCode ?? undefined,
			[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_STREET]: pivMainAddress?.street ?? undefined,
			[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_HOUSE_NUMBER]:
				pivMainAddress?.houseNumber ?? undefined,
			[JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR]:
				isRepresentativeSameAsInvestor,
			[JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME]:
				payload.representativePerson?.firstName ?? undefined,
			[JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME]:
				payload.representativePerson?.lastName ?? undefined,
			[JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_EMAIL]:
				payload.representativePerson?.email ?? undefined,
			// Controlling Parties flow (MIP-2558), repopulated from saved data.
			[JOIN_ALLOCATION_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED]:
				isControllingPartyRequired,
			[JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES]: controllingPartyEntries,
			[JOIN_ALLOCATION_PIV_INFO_FORM.SIGNATORY_CHOICE]: signatoryChoice,
			[JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME]: '',
			[JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME]: '',
			[JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL]: '',
			[JOIN_ALLOCATION_PIV_INFO_FORM.SAVED_SIGNATORY]: savedSignatory,
		});
		void pivInformationForm.trigger().then(() => pivInformationForm.clearErrors());
	}, [getInvestorDataRequest?.response?.payload]);

	// Keep the gating flag in the form in sync once the kyc setting resolves
	// (it may arrive after the payload-driven reset above).
	useEffect(() => {
		pivInformationForm.setValue(
			JOIN_ALLOCATION_PIV_INFO_FORM.IS_CONTROLLING_PARTY_REQUIRED,
			isControllingPartyRequired
		);
	}, [isControllingPartyRequired]);

	// A person can't hold the same role twice — block continuing while the list
	// contains a duplicate (person + relationship type) entry (MIP-2558). Only
	// while investing via a vehicle: when personal, the section is hidden and a
	// stale duplicate must not dead-lock the Continue button (MIP-2667).
	const watchedControllingParties = pivInformationForm.watch(
		JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES
	);
	const watchedIsPiv = pivInformationForm.watch(JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV);
	const hasDuplicateControllingParty =
		watchedIsPiv && hasDuplicateControllingPartyType(watchedControllingParties ?? []);

	// Continue stays clickable while fields are invalid: the click runs
	// validation, marks every offending field red and focuses the first one.
	// Disabling on !isValid hid all of that — load-time errors are cleared, so
	// the investor faced a dead button with no visible reason (MIP-2655). Only
	// the duplicate-role case still disables, since it renders its own message.
	useEffect(() => {
		setIsContinueDisabled(hasDuplicateControllingParty);

		return () => {
			setIsContinueDisabled(false);
		};
	}, [hasDuplicateControllingParty]);

	useEffect(() => {
		void pivInformationForm.trigger(JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV);
		const isPIVVisible = pivInformationForm.getValues(JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV);
		if (isPIVVisible) {
			return;
		}
		//  27.09.2022 dslaby PIV is not visible - clear errors related to it
		// https://gitlab.com/d6275/dealstack/-/issues/437
		pivInformationForm.clearErrors([
			JOIN_ALLOCATION_PIV_INFO_FORM.PIV_NAME,
			JOIN_ALLOCATION_PIV_INFO_FORM.PIV_REG_NUMBER,
			JOIN_ALLOCATION_PIV_INFO_FORM.PIV_COUNTRY,
			JOIN_ALLOCATION_PIV_INFO_FORM.PIV_CITY,
			JOIN_ALLOCATION_PIV_INFO_FORM.PIV_POST_CODE,
			JOIN_ALLOCATION_PIV_INFO_FORM.PIV_STREET,
			JOIN_ALLOCATION_PIV_INFO_FORM.PIV_HOUSE_NUMBER,
			JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR,
			JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME,
			JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME,
			JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_EMAIL,
			JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES,
			JOIN_ALLOCATION_PIV_INFO_FORM.SIGNATORY_CHOICE,
			JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME,
			JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME,
			JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL,
		]);
	}, [pivInformationForm.watch(JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV)]);

	useEffect(() => {
		const payload = getInvestorDataRequest?.response?.payload;
		const { getValues, resetField } = pivInformationForm;
		const formValues = getValues();

		if (
			!formValues[JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR] ||
			!formValues[JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV]
		) {
			return;
		}

		resetField(JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME, {
			defaultValue: payload?.user?.firstName?.trim(),
		});
		resetField(JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME, {
			defaultValue: payload?.user?.lastName?.trim(),
		});
		resetField(JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_EMAIL, {
			defaultValue: payload?.user?.email?.trim(),
		});
	}, [
		pivInformationForm.watch(JOIN_ALLOCATION_PIV_INFO_FORM.IS_REPRESENTATIVE_SAME_AS_INVESTOR),
		pivInformationForm.watch(JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV),
	]);

	const onSubmit = (): Promise<WizardSaveResult> =>
		new Promise((resolve) => {
			setIsLoading(true);
			const formValues = pivInformationForm.getValues();
			const isPIVInvestment = formValues[JOIN_ALLOCATION_PIV_INFO_FORM.IS_PIV];
			const payload = getInvestorDataRequest?.response?.payload;

			// In the controlling-party flow the Legal Representative is the chosen
			// authorised signatory; otherwise it's the legacy representative fields.
			const legalRepresentative = isControllingPartyRequired
				? {
						...resolveAuthorisedSignatory(
							formValues[JOIN_ALLOCATION_PIV_INFO_FORM.SIGNATORY_CHOICE],
							formValues[JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES],
							{
								firstName:
									formValues[
										JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_FIRST_NAME
									],
								lastName:
									formValues[
										JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_LAST_NAME
									],
								email: formValues[
									JOIN_ALLOCATION_PIV_INFO_FORM.OTHER_SIGNATORY_EMAIL
								],
							},
							{
								firstName: payload?.user?.firstName,
								lastName: payload?.user?.lastName,
								email: payload?.user?.email,
							},
							formValues[JOIN_ALLOCATION_PIV_INFO_FORM.SAVED_SIGNATORY]
						),
						relationId: payload?.representativePerson?.relationId,
					}
				: {
						firstName:
							formValues[
								JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_FIRST_NAME
							]?.trim(),
						lastName:
							formValues[
								JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_LAST_NAME
							]?.trim(),
						email: formValues[
							JOIN_ALLOCATION_PIV_INFO_FORM.REPRESENTATIVE_EMAIL
						]?.trim(),
						relationId: payload?.representativePerson?.relationId,
					};

			// In the controlling-party flow, each entry is persisted as a
			// UBO/PSC/Director relation (with address + tax residence) against the
			// PIV person by the backend.
			const controllingParties = isControllingPartyRequired
				? toControllingPartyDtos(
						formValues[JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES]
					)
				: undefined;

			updateInvestorVehicleDataRequest
				.fetch(
					isPIVInvestment
						? {
								kycWorkflowId,
								draftPivId: payload?.draftPivPerson?.id,
								controllingParties,
								legalRepresentative,
								personalInvestmentVehicle: {
									pivName:
										formValues[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_NAME]?.trim(),
									corporateRegistrationNumber:
										formValues[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_REG_NUMBER],
									address: {
										country:
											formValues[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_COUNTRY],
										city: formValues[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_CITY],
										postCode:
											formValues[JOIN_ALLOCATION_PIV_INFO_FORM.PIV_POST_CODE],
										street: formValues[
											JOIN_ALLOCATION_PIV_INFO_FORM.PIV_STREET
										],
										houseNumber:
											formValues[
												JOIN_ALLOCATION_PIV_INFO_FORM.PIV_HOUSE_NUMBER
											],
									},
								},
							}
						: { kycWorkflowId }
				)
				.onAnyError(() => {
					resolve(SAVE_FAILED);
					setIsLoading(false);
				})
				.onLocalError((error) => {
					showToastV2({
						title: isTinInUseError(error)
							? tInvestor('investment.toast.tin_already_in_use')
							: tInvestor('investment.toast.failed_save_company_information'),
						state: 'error',
					});
				})
				.onSuccess(async () => {
					await getInvestorDataRequest.fetch().concludeWithPromise();
					await queryClient.invalidateQueries({
						queryKey: [API_PATHS.SHAREHOLDER_INVESTMENT_BY_MIP_ID_MIPID_GET],
					});
					await queryClient.invalidateQueries({
						queryKey: [API_PATHS.SHAREHOLDER_KYC_KYC_FORMS_DOCUMENTWORKFLOW_GET],
					});
					if (kycWorkflowId) {
						await queryClient.invalidateQueries({
							queryKey: [
								formatUnicorn(
									API_PATHS.SHAREHOLDER_KYC_STANDALONE_KYCWORKFLOWID_GET,
									{ kycWorkflowId }
								),
							],
						});
					}
					showToastV2({ title: tInvestor('investment.toast.data_saved') });
					resolve(SAVE_OK);
					setIsLoading(false);
				});
		});

	useWizardStep(async (intent) => {
		if (intent !== 'next') {
			updateBackupData(
				INVESTOR_BACKUP_PROPERTY.pivInformation,
				pivInformationForm.getValues()
			);
			return SAVE_OK;
		}
		// Validate via handleSubmit so a failure marks every invalid field and focuses the first one.
		let isValid = false;
		await pivInformationForm.handleSubmit(async () => {
			isValid = true;
		})();
		if (!isValid) {
			return SAVE_INVALID;
		}
		const isDirty = !isEmpty(pivInformationForm.formState.dirtyFields);
		// In the controlling-party flow always submit: field-array edits do not reliably flip RHF dirty state (MIP-2558).
		if (!isDirty && !dataBackup.pivInformation && !isControllingPartyRequired) {
			return SAVE_OK;
		}
		removeBackupData(INVESTOR_BACKUP_PROPERTY.pivInformation);
		return onSubmit();
	});

	return {
		pivInformationForm,
		onSubmit,
		isControllingPartyRequired,
		investorPersonId: getInvestorDataRequest?.response?.payload?.personId,
	};
};
